import React, { useRef, useCallback, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { StepProps } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const PhotoSection = styled.div`
  ${tw`mb-6`}
`;

const SectionHeader = styled.div`
  ${tw`flex items-center justify-between mb-3`}
`;

const SectionTitle = styled.h3`
  ${tw`font-semibold text-text-primary`}
`;

const SectionLabel = styled.span`
  ${tw`text-sm text-text-secondary`}
`;

const RequiredLabel = styled.span`
  ${tw`text-sm text-red-500`}
`;

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-4 gap-3`}
`;

const PhotoCard = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden`}
`;

const Photo = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const RemoveButton = styled.button`
  ${tw`absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center`}
  &:hover {
    ${tw`bg-red-600`}
  }
`;

const AddPhotoButton = styled.button`
  ${tw`aspect-square rounded-lg border-2 border-dashed border-border-default flex flex-col items-center justify-center text-text-tertiary gap-1`}
  &:hover {
    ${tw`border-black text-black`}
  }
`;

const AddPhotoIcon = styled(FontAwesomeIcon)`
  ${tw`text-2xl`}
`;

const AddPhotoText = styled.span`
  ${tw`text-xs`}
`;

const TripPickerToggle = styled.button`
  ${tw`text-sm text-black font-medium mt-2 hover:text-black`}
  &:disabled {
    ${tw`text-text-tertiary cursor-not-allowed`}
  }
`;

const TripPickerPanel = styled.div`
  ${tw`mt-3 p-3 rounded-lg border border-border-default bg-bg-secondary`}
`;

const TripPickerLabel = styled.p`
  ${tw`text-xs text-text-secondary mb-2 m-0`}
`;

const TripThumbGrid = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const TripThumb = styled.button<{ selected: boolean }>`
  ${tw`relative w-16 h-16 rounded-md overflow-hidden border-2 flex-shrink-0`}
  border-color: ${({ selected }) => (selected ? '#db2777' : 'var(--color-border)')};
`;

const TripThumbImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const TripThumbBadge = styled.span`
  ${tw`absolute bottom-0 left-0 right-0 text-[10px] text-white bg-black/60 px-0.5 py-0.5 truncate`}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const HiddenInput = styled.input`
  ${tw`hidden`}
`;

const MAX_PHOTOS = 10;

type Section = 'before' | 'after';

const PhotosUploadStep: React.FC<StepProps> = ({
  formData,
  updateFormData,
  onNext,
  tripPickerItems = [],
  tripPhotoAllowlist,
}) => {
  const beforeInputRef = useRef<HTMLInputElement>(null);
  const afterInputRef = useRef<HTMLInputElement>(null);
  const [tripPickerOpenFor, setTripPickerOpenFor] = useState<Section | null>(null);

  const hasTripPicker = tripPickerItems.length > 0;

  const toggleTripPicker = (section: Section) => {
    setTripPickerOpenFor(prev => (prev === section ? null : section));
  };

  const isTripSelected = (section: Section, resolved: string) => {
    const slots = section === 'before' ? formData.beforeEvidenceSlots : formData.afterEvidenceSlots;
    return slots.some(s => s.kind === 'trip' && s.url === resolved);
  };

  const toggleTripInSection = (section: Section, resolved: string) => {
    if (!tripPhotoAllowlist?.has(resolved)) {
      toast.error('Invalid photo selection');
      return;
    }
    const key = section === 'before' ? 'beforeEvidenceSlots' : 'afterEvidenceSlots';
    const slots = section === 'before' ? formData.beforeEvidenceSlots : formData.afterEvidenceSlots;
    const existingIdx = slots.findIndex(s => s.kind === 'trip' && s.url === resolved);
    if (existingIdx >= 0) {
      updateFormData({
        [key]: slots.filter((_, i) => i !== existingIdx),
      });
      return;
    }
    if (slots.length >= MAX_PHOTOS) {
      toast.info(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    updateFormData({
      [key]: [...slots, { kind: 'trip', url: resolved }],
    });
  };

  const handleBeforePhotoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const remainingSlots = MAX_PHOTOS - formData.beforeEvidenceSlots.length;
      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      if (filesToAdd.length < files.length) {
        toast.info(`Maximum ${MAX_PHOTOS} photos allowed`);
      }

      const newSlots = [
        ...formData.beforeEvidenceSlots,
        ...filesToAdd.map(file => ({
          kind: 'upload' as const,
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ];

      updateFormData({ beforeEvidenceSlots: newSlots });

      if (beforeInputRef.current) {
        beforeInputRef.current.value = '';
      }
    },
    [formData.beforeEvidenceSlots, updateFormData],
  );

  const handleAfterPhotoSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const remainingSlots = MAX_PHOTOS - formData.afterEvidenceSlots.length;
      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      if (filesToAdd.length < files.length) {
        toast.info(`Maximum ${MAX_PHOTOS} photos allowed`);
      }

      const newSlots = [
        ...formData.afterEvidenceSlots,
        ...filesToAdd.map(file => ({
          kind: 'upload' as const,
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ];

      updateFormData({ afterEvidenceSlots: newSlots });

      if (afterInputRef.current) {
        afterInputRef.current.value = '';
      }
    },
    [formData.afterEvidenceSlots, updateFormData],
  );

  const handleRemoveBeforeSlot = useCallback(
    (index: number) => {
      const slot = formData.beforeEvidenceSlots[index];
      if (slot?.kind === 'upload') URL.revokeObjectURL(slot.previewUrl);
      updateFormData({
        beforeEvidenceSlots: formData.beforeEvidenceSlots.filter((_, i) => i !== index),
      });
    },
    [formData.beforeEvidenceSlots, updateFormData],
  );

  const handleRemoveAfterSlot = useCallback(
    (index: number) => {
      const slot = formData.afterEvidenceSlots[index];
      if (slot?.kind === 'upload') URL.revokeObjectURL(slot.previewUrl);
      updateFormData({
        afterEvidenceSlots: formData.afterEvidenceSlots.filter((_, i) => i !== index),
      });
    },
    [formData.afterEvidenceSlots, updateFormData],
  );

  const canContinue = formData.afterEvidenceSlots.length > 0;

  return (
    <Container>
      <Title>Photos and documents</Title>
      <Subtitle>
        Add new photos with <strong>Add</strong>
        {hasTripPicker
          ? ', or open Choose from trip photos to pick existing trip start/end images from this booking.'
          : '.'}{' '}
        Before photos are optional; at least one after photo is required.
      </Subtitle>

      <PhotoSection>
        <SectionHeader>
          <SectionTitle>Before photos</SectionTitle>
          <SectionLabel>(Optional)</SectionLabel>
        </SectionHeader>
        <PhotoGrid>
          {formData.beforeEvidenceSlots.map((slot, index) => (
            <PhotoCard key={`before-${index}-${slot.kind}`}>
              <Photo
                src={slot.kind === 'trip' ? slot.url : slot.previewUrl}
                alt={`Before ${index + 1}`}
              />
              <RemoveButton onClick={() => handleRemoveBeforeSlot(index)}>
                <FontAwesomeIcon icon={faTrash} size="xs" />
              </RemoveButton>
            </PhotoCard>
          ))}
          {formData.beforeEvidenceSlots.length < MAX_PHOTOS && (
            <AddPhotoButton onClick={() => beforeInputRef.current?.click()}>
              <AddPhotoIcon icon={faPlus} />
              <AddPhotoText>Add</AddPhotoText>
            </AddPhotoButton>
          )}
        </PhotoGrid>
        {hasTripPicker && (
          <>
            <TripPickerToggle
              type="button"
              disabled={formData.beforeEvidenceSlots.length >= MAX_PHOTOS}
              onClick={() => toggleTripPicker('before')}
            >
              {tripPickerOpenFor === 'before' ? 'Hide trip photos' : 'Choose from trip photos'}
            </TripPickerToggle>
            {tripPickerOpenFor === 'before' && (
              <TripPickerPanel>
                <TripPickerLabel>
                  Tap a thumbnail to add or remove it from before photos ({tripPickerItems.length}{' '}
                  available).
                </TripPickerLabel>
                <TripThumbGrid>
                  {tripPickerItems.map(item => (
                    <TripThumb
                      key={item.key}
                      type="button"
                      selected={isTripSelected('before', item.resolved)}
                      onClick={() => toggleTripInSection('before', item.resolved)}
                      title={item.label}
                    >
                      <TripThumbImg src={item.resolved} alt="" />
                      <TripThumbBadge>{item.label}</TripThumbBadge>
                    </TripThumb>
                  ))}
                </TripThumbGrid>
              </TripPickerPanel>
            )}
          </>
        )}
        <HiddenInput
          ref={beforeInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleBeforePhotoSelect}
        />
      </PhotoSection>

      <PhotoSection>
        <SectionHeader>
          <SectionTitle>After photos</SectionTitle>
          <RequiredLabel>(Required*)</RequiredLabel>
        </SectionHeader>
        <PhotoGrid>
          {formData.afterEvidenceSlots.map((slot, index) => (
            <PhotoCard key={`after-${index}-${slot.kind}`}>
              <Photo
                src={slot.kind === 'trip' ? slot.url : slot.previewUrl}
                alt={`After ${index + 1}`}
              />
              <RemoveButton onClick={() => handleRemoveAfterSlot(index)}>
                <FontAwesomeIcon icon={faTrash} size="xs" />
              </RemoveButton>
            </PhotoCard>
          ))}
          {formData.afterEvidenceSlots.length < MAX_PHOTOS && (
            <AddPhotoButton onClick={() => afterInputRef.current?.click()}>
              <AddPhotoIcon icon={faPlus} />
              <AddPhotoText>Add</AddPhotoText>
            </AddPhotoButton>
          )}
        </PhotoGrid>
        {hasTripPicker && (
          <>
            <TripPickerToggle
              type="button"
              disabled={formData.afterEvidenceSlots.length >= MAX_PHOTOS}
              onClick={() => toggleTripPicker('after')}
            >
              {tripPickerOpenFor === 'after' ? 'Hide trip photos' : 'Choose from trip photos'}
            </TripPickerToggle>
            {tripPickerOpenFor === 'after' && (
              <TripPickerPanel>
                <TripPickerLabel>
                  Tap a thumbnail to add or remove it from after photos. At least one after photo is
                  required to continue.
                </TripPickerLabel>
                <TripThumbGrid>
                  {tripPickerItems.map(item => (
                    <TripThumb
                      key={item.key}
                      type="button"
                      selected={isTripSelected('after', item.resolved)}
                      onClick={() => toggleTripInSection('after', item.resolved)}
                      title={item.label}
                    >
                      <TripThumbImg src={item.resolved} alt="" />
                      <TripThumbBadge>{item.label}</TripThumbBadge>
                    </TripThumb>
                  ))}
                </TripThumbGrid>
              </TripPickerPanel>
            )}
          </>
        )}
        <HiddenInput
          ref={afterInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleAfterPhotoSelect}
        />
      </PhotoSection>

      <ContinueButton disabled={!canContinue} onClick={onNext}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default PhotosUploadStep;
